/**
 * Pure bridge functions between TanStack table state and the library-list API
 * query params. ZERO React surface; ZERO MUI imports.
 *
 * Why this file exists
 * --------------------
 * TanStack and the API speak different dialects:
 *   - TanStack `PaginationState.pageIndex` is 0-based; the API's `page` is
 *     1-based.
 *   - TanStack `SortingState` is `Array<{ id, desc }>`; the API takes
 *     `{ sort_by, order }`.
 *   - TanStack `ColumnFiltersState` is `Array<{ id, value }>`; the API folds
 *     a column filter into the same `{ q, sort_by, order }` triple as the
 *     search bar.
 *
 * Mutex rule (R6)
 * ---------------
 * The search-bar and the column-filter share the API's `q`/`sort_by`/`order`
 * triple — they are MUTUALLY EXCLUSIVE. The search bar wins:
 *   - When the search bar is non-empty: `q = search`, NO `sort_by`, NO `order`.
 *   - Otherwise: column filter + sort flow through normally.
 * Centralizing the mutex in {@link filterStateToParams} keeps a spread-order
 * mistake from leaking a stale `sort_by` when the user types in the search bar.
 */
import type {
    ColumnFiltersState,
    PaginationState,
    SortingState,
} from "@tanstack/react-table";

export interface PageParams {
    page: number;
    size: number;
}

export function paginationStateToParams(p: PaginationState): PageParams {
    return { page: p.pageIndex + 1, size: p.pageSize };
}

export interface ListQueryParams {
    q?: string;
    sort_by?: string;
    order?: "asc" | "desc";
}

/**
 * Resolve `{ q, sort_by, order }` from TanStack column filters + sort + the
 * search-bar text. The search bar takes priority and drops any column-filter
 * contribution (R6 mutex). When the search bar is empty, the first column
 * filter (if any) folds into `{ q, sort_by }` and any active sort flows
 * through as `order`.
 */
export function filterStateToParams(
    columnFilters: ColumnFiltersState,
    searchQuery: string,
    sorting: SortingState = []
): ListQueryParams {
    const trimmed = searchQuery.trim();
    if (trimmed) {
        return { q: trimmed };
    }

    const sort = sorting[0];
    const order = sort ? (sort.desc ? "desc" : "asc") : undefined;

    const first = columnFilters[0];
    if (first && typeof first.value === "string" && first.value.length > 0) {
        const out: ListQueryParams = { q: first.value, sort_by: first.id };
        if (order) out.order = order;
        return out;
    }

    return sort ? { sort_by: sort.id, order } : {};
}
